// Portfolio quoting (Session AAU, QUOTING PORTFOLIO Loop 2). Group several SITE sub-quotes for one
// customer into a grouped quotation and roll them up with the two-layer discount (per-site multi-lift
// + multi-site loyalty, both server-priced, capped 20% per site). This page lists portfolios and
// creates them; PortfolioDetailPage shows the roll-up and manages membership + delete.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { QuoteListItem, QuotePortfolioDetail, QuotePortfolioListItem } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { Button, buttonClasses } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";
import { RecordHeader } from "../../components/RecordHeader";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { ListToolbar } from "../../components/ListToolbar";
import { SortableHeader } from "../../components/SortableHeader";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { formatDate } from "../../lib/format";

const gbp = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;
const pct = (n: number) => `${(n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 1)}%`;

const CARD = "rounded-ele border border-ele-purple-accent bg-ele-surface p-4";
const BTN = "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong";

const PORTFOLIO_SEARCH_ACCESSORS: SearchAccessors<QuotePortfolioListItem> = [(p) => p.name, (p) => p.customerName];
const PORTFOLIO_SORT_ACCESSORS: SortAccessors<QuotePortfolioListItem> = {
  portfolio: (p) => p.name,
  customer: (p) => p.customerName,
  quotes: (p) => p.quoteCount,
  created: (p) => p.createdAt,
};
const PORTFOLIO_SORT_OPTIONS = [
  { key: "portfolio", label: "Portfolio" },
  { key: "customer", label: "Customer" },
  { key: "quotes", label: "Site quotes" },
  { key: "created", label: "Created" },
];

// The statuses eligible to group into a portfolio (a live/finished quote is not repriced here).
const GROUPABLE_PAGE = 50; // Session SC2C: how many groupable quotes the pickers below show at a time (the server filters the status).

export function PortfoliosPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<QuotePortfolioListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Chosen once at mount; see the note on PortfolioDetailPage's copy of this line.
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("portfolios");

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.quotePortfolios.list());
    } catch {
      setError("Could not load portfolios.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const shown = rows ? sortRows(filterRows(rows, search, PORTFOLIO_SEARCH_ACCESSORS), PORTFOLIO_SORT_ACCESSORS, sort) : [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <PageHeader
        className="mb-6"
        title="Quote portfolios"
        subtitle="Group a customer's site quotes and roll them up with the portfolio discount."
        actions={<Button variant="secondary" onClick={() => setCreating(true)}>New portfolio</Button>}
      />

      {error && <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface p-4 text-ele-strong">{error}</p>}

      {rows !== null && rows.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search portfolio or customer..."
          searchLabel="Search portfolios"
          resultCount={{ shown: shown.length, total: rows.length, noun: "portfolios" }}
          sort={{ state: sort, options: PORTFOLIO_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {rows === null ? (
        <Loader />
      ) : rows.length === 0 ? (
        <EmptyState message="No portfolios yet. Use New portfolio to group a customer's site quotes." />
      ) : shown.length === 0 ? (
        <EmptyState role="status" message={`No portfolios match "${search.trim()}".`} />
      ) : mobileCards ? (
        /* Session AIB, mobile audit M1. Only one of this table's five columns was hidden below md,
           against a platform norm of hiding three to six, and the page post-dates the Session AHB
           card pass that ten other list pages got. The card also brings Created back, which was the
           one column the priority-hiding dropped. */
        <MobileCardList label="Quote portfolios">
          {shown.map((p) => (
            <MobileListCard
              key={p.id}
              to={`/office/quote-portfolios/${p.id}`}
              primary={p.name}
              secondary={p.customerName ?? "Not linked"}
              meta={`Created ${formatDate(p.createdAt)}`}
              right={<span className="text-ele-body">{p.quoteCount} site quote{p.quoteCount === 1 ? "" : "s"}</span>}
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Portfolio" colKey="portfolio" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site quotes" colKey="quotes" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Created" colKey="created" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr
                  key={p.id}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                    navigate(`/office/quote-portfolios/${p.id}`);
                  }}
                  className="group cursor-pointer border-t border-ele-purple-accent hover:bg-ele-row-hover"
                >
                  <td className="px-3 py-2 align-top font-heading text-ele-strong">
                    <Link to={`/office/quote-portfolios/${p.id}`} className="text-ele-green hover:underline">{p.name}</Link>
                  </td>
                  <td className="px-3 py-2 align-top text-ele-body">{p.customerName ?? "Not linked"}</td>
                  <td className="px-3 py-2 align-top text-ele-body">{p.quoteCount}</td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{formatDate(p.createdAt)}</td>
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreatePortfolioModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void load(); }} />}
    </div>
  );
}

function CreatePortfolioModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A failed quotes fetch used to fall silently to an empty list, leaving the office staring at an
  // empty groupable-quotes picker with nothing to say why. Load through the shared options hook so a
  // failure shows an inline "Could not load" + Retry instead (Session AEH B3).
  // Session SC2C: the quotes are SEARCHED on the server (the newest 50 groupable quotes, narrowed by what is typed), not all loaded to be filtered here.
  const [quoteSearch, setQuoteSearch] = useState("");
  const debouncedQuoteSearch = useDebouncedValue(quoteSearch.trim(), 250);
  const { data: quotePage, loading: quotesLoading, error: quotesError, retry: retryQuotes } = useOptionsLoader(
    () => api.quotes.listPage({ filter: "groupable", ...(debouncedQuoteSearch ? { q: debouncedQuoteSearch } : {}), limit: GROUPABLE_PAGE, offset: 0 }),
    [debouncedQuoteSearch],
  );
  const quotes = quotePage?.items ?? null;

  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const submit = async () => {
    if (!name.trim()) { setError("Give the portfolio a name."); return; }
    setBusy(true); setError(null);
    try {
      await api.quotePortfolios.create({
        name: name.trim(),
        customerName: customerName.trim() || undefined,
        quoteIds: picked.size > 0 ? [...picked] : undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? "Could not create the portfolio." : "Could not create the portfolio.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell ariaLabel="New portfolio" onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="p-6">
        <h2 className="mb-4 font-heading text-xl text-ele-strong">New portfolio</h2>
        {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger p-3 text-sm text-ele-strong">{error}</p>}
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Portfolio name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-ele-strong outline-none focus:border-ele-green" placeholder="e.g. Acme estate 2026" />
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Customer (optional)</label>
        <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-ele-strong outline-none focus:border-ele-green" placeholder="Customer name" />
        <p className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-blue">Site quotes to group</p>
        <input
          type="search"
          value={quoteSearch}
          onChange={(e) => setQuoteSearch(e.target.value)}
          aria-label="Search quotes to group"
          placeholder="Search by reference, customer or site..."
          className="mb-2 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
        />
        <div className="mb-4 max-h-56 overflow-y-auto rounded-ele border border-ele-purple-accent">
          {quotesLoading ? (
            <Loader shape="inline" label="Loading quotes..." className="p-3" />
          ) : quotesError ? (
            <div className="p-3 text-sm">
              <p className="text-ele-body">Could not load the quotes.</p>
              <button type="button" onClick={retryQuotes} className="mt-2 font-menu text-xs tracking-wide text-ele-blue underline hover:text-ele-strong">Retry</button>
            </div>
          ) : (quotes ?? []).length === 0 ? (
            <p className="p-3 text-sm text-ele-body">No groupable quotes. Create draft or sent quotes first, then group them.</p>
          ) : (
            (quotes ?? []).map((q) => (
              <label key={q.id} className="flex cursor-pointer items-center gap-3 border-t border-ele-purple-accent px-3 py-2 text-sm first:border-t-0 hover:bg-ele-row-hover">
                <input type="checkbox" checked={picked.has(q.id)} onChange={() => toggle(q.id)} className="accent-ele-green" />
                <span className="font-heading text-ele-strong">{q.quoteRef}</span>
                <span className="text-ele-body">{q.site?.name ?? q.customerName ?? "No site"}</span>
                <span className="ml-auto"><StatusPill domain="quote" status={q.status} /></span>
              </label>
            ))
          )}
        </div>
        {quotePage && quotePage.total > quotePage.items.length && (
          <p className="-mt-2 mb-4 text-xs text-ele-body">Showing the newest {quotePage.items.length} of {quotePage.total}. Search to find the others.</p>
        )}
        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" className={`${BTN} ${MODAL_ACTION_BTN}`} onClick={onClose} disabled={busy}>Cancel</button>
          <Button variant="secondary" className={MODAL_ACTION_BTN} onClick={submit} disabled={busy}>{busy ? "Creating..." : "Create portfolio"}</Button>
        </div>
    </ModalShell>
  );
}

export function PortfolioDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<QuotePortfolioDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Chosen ONCE at mount, never re-evaluated on resize: exactly one of the table and the card list
  // is ever in the DOM, so a row's state can never exist twice (src/lib/mobileLayout.ts). jsdom has
  // no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await api.quotePortfolios.get(id));
    } catch {
      setError("Could not load this portfolio.");
    }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const detach = useCallback(async (quoteId: string) => {
    await api.quotePortfolios.detachQuote(id, quoteId);
    void load();
  }, [id, load]);

  const rollup = detail?.rollup;
  const loyaltyRateHint = useMemo(() => {
    if (!rollup || rollup.sites.length === 0) return null;
    return rollup.sites[0]!.discount.loyalty;
  }, [rollup]);

  if (error) return <div className="mx-auto max-w-4xl px-4 py-8"><p role="alert" className="rounded-ele border border-ele-danger p-4 text-ele-strong">{error}</p></div>;
  if (!detail || !rollup) return <div className="mx-auto max-w-4xl px-4 py-8"><Loader shape="panel" /></div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <RecordHeader
        className="mb-6"
        backTo="/office/quote-portfolios"
        backLabel="Back to portfolios"
        title={detail.name}
        subtitle={<>{detail.customerName ?? "No customer linked"} · {rollup.customerSiteCount} site{rollup.customerSiteCount === 1 ? "" : "s"} with ELE{loyaltyRateHint != null && loyaltyRateHint > 0 ? ` · ${pct(loyaltyRateHint)} loyalty discount` : ""}</>}
        actions={<Button variant="destructive" onClick={() => setDeleting(true)}>Delete</Button>}
      />

      {rollup.sites.length === 0 ? (
        <EmptyState message="No priced site quotes are grouped here yet. Attach quotes from the list below." />
      ) : mobileCards ? (
        /* Session AIB, mobile audit H3. This roll-up was the only list table in the office with no
           mobile treatment of any kind: seven columns, no max-md:hidden, no card, no scroll wrapper.
           .ele-list-table is table-layout:fixed with no colgroup here, so at 343px the seven columns
           divided into ~49px each and ~25px after each cell's px-3, which truncated "Portfolio
           discount" and "Site total (ex VAT)" to about three characters and every money value to
           roughly "£1...". Comparing per-site money across a customer's portfolio is the entire
           purpose of the page, so that was the one thing a phone could not do.
           The card carries MORE than the mobile table did, not less: the site total is the right-rail
           headline, and the maintenance and discount figures move into the details block where they
           have a full line each and cannot be clipped. */
        <>
          <MobileCardList label="Sites in this portfolio">
            {rollup.sites.map((s) => (
              <MobileListCard
                key={s.quoteId}
                primary={s.siteName ?? s.quoteRef}
                secondary={`${s.tier} tier · ${s.liftCount} lift${s.liftCount === 1 ? "" : "s"}`}
                meta={`${s.quoteRef} · ${s.status}`}
                right={<span className="font-heading text-ele-green">{gbp(s.siteAnnual)}</span>}
                details={
                  <dl className="space-y-1 text-xs">
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="text-ele-body">Maintenance</dt>
                      <dd className="text-ele-body">{gbp(s.maintenanceAnnual)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="text-ele-body">Portfolio discount</dt>
                      <dd className="text-ele-body">{s.loyaltyDiscount > 0 ? `- ${gbp(s.loyaltyDiscount)}` : "-"}</dd>
                    </div>
                    <p className="font-menu text-[0.6rem] uppercase text-ele-body opacity-70">
                      {pct(s.discount.multiLift)} lifts + {pct(s.discount.allowedLoyalty)} loyalty{s.discount.capped ? " (capped 20%)" : ""}
                    </p>
                  </dl>
                }
                actions={
                  /* Two-step arming (Session AEH A2): the first click arms, the second detaches.
                     ConfirmButton bakes in its own `max-sm:min-h-11` tap-target floor, but max-sm
                     is under 640px while this card branch runs to 767px, so the floor is restated
                     unprefixed here: it applies only to this instance, which exists only inside the
                     card. inline-flex + items-center because a min-height does nothing to an inline
                     button (which is why the component pairs them too). */
                  <ConfirmButton
                    label="Detach"
                    confirmLabel="Confirm detach?"
                    className="inline-flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-danger underline decoration-dotted hover:text-ele-strong"
                    onConfirm={() => void detach(s.quoteId)}
                  />
                }
              />
            ))}
          </MobileCardList>
          {/* The table's tfoot has no card equivalent, and its colSpan={3} is tied to the 7-column
              grid, so the portfolio total becomes its own summary block rather than a final card:
              it is a total, not another site. */}
          <div className={`${CARD} mt-3`}>
            <dl className="space-y-1 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ele-body">Maintenance</dt>
                <dd className="text-ele-body">{gbp(rollup.subtotalAnnual)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ele-body">Portfolio discount</dt>
                <dd className="text-ele-body">{rollup.totalLoyaltyDiscount > 0 ? `- ${gbp(rollup.totalLoyaltyDiscount)}` : "-"}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-ele-purple-accent pt-1">
                <dt className="font-heading text-ele-strong">Portfolio total</dt>
                <dd className="font-heading text-ele-green">{gbp(rollup.grandAnnual)}</dd>
              </div>
            </dl>
          </div>
        </>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Site</th>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2 text-right">Lifts</th>
                <th className="px-3 py-2 text-right">Maintenance</th>
                <th className="px-3 py-2 text-right">Portfolio discount</th>
                <th className="px-3 py-2 text-right">Site total (ex VAT)</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rollup.sites.map((s) => (
                <tr key={s.quoteId} className="border-t border-ele-purple-accent">
                  <td className="px-3 py-2 align-top font-heading text-ele-strong">{s.siteName ?? s.quoteRef}<span className="block font-menu text-[0.65rem] uppercase text-ele-body">{s.quoteRef} · {s.status}</span></td>
                  <td className="px-3 py-2 align-top capitalize text-ele-body">{s.tier}</td>
                  <td className="px-3 py-2 align-top text-right text-ele-body">{s.liftCount}</td>
                  <td className="px-3 py-2 align-top text-right text-ele-body">{gbp(s.maintenanceAnnual)}</td>
                  <td className="px-3 py-2 align-top text-right text-ele-body">
                    {s.loyaltyDiscount > 0 ? `- ${gbp(s.loyaltyDiscount)}` : "-"}
                    <span className="block font-menu text-[0.6rem] uppercase text-ele-body">
                      {pct(s.discount.multiLift)} lifts + {pct(s.discount.allowedLoyalty)} loyalty{s.discount.capped ? " (capped 20%)" : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top text-right font-heading text-ele-green">{gbp(s.siteAnnual)}</td>
                  <td className="px-3 py-2 align-top text-right">
                    {/* Two-step arming (Session AEH A2): the first click arms, the second detaches. */}
                    <ConfirmButton
                      label="Detach"
                      confirmLabel="Confirm detach?"
                      className="font-menu text-[0.65rem] tracking-wide text-ele-danger underline decoration-dotted hover:text-ele-strong"
                      onConfirm={() => void detach(s.quoteId)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-ele-purple-accent bg-ele-inset">
              <tr>
                <td className="px-3 py-2 font-heading text-ele-strong" colSpan={3}>Portfolio total</td>
                <td className="px-3 py-2 text-right text-ele-body">{gbp(rollup.subtotalAnnual)}</td>
                <td className="px-3 py-2 text-right text-ele-body">{rollup.totalLoyaltyDiscount > 0 ? `- ${gbp(rollup.totalLoyaltyDiscount)}` : "-"}</td>
                <td className="px-3 py-2 text-right font-heading text-ele-green">{gbp(rollup.grandAnnual)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-ele-body">
        The per-site multi-lift discount is already in each quote. The multi-site loyalty discount is added on top and capped so the combined discount at any one site never exceeds 20%. Additional items, care equipment and surcharges are never discounted.
      </p>

      <AttachQuotesPanel portfolioId={id} existing={new Set(rollup.sites.map((s) => s.quoteId))} onChanged={load} />

      {deleting && <DeletePortfolioModal name={detail.name} onCancel={() => setDeleting(false)} onDeleted={() => navigate("/office/quote-portfolios")} id={id} />}
    </div>
  );
}

function AttachQuotesPanel({ portfolioId, existing, onChanged }: { portfolioId: string; existing: Set<string>; onChanged: () => Promise<void> | void }) {
  const [quotes, setQuotes] = useState<QuoteListItem[] | null>(null);
  useEffect(() => {
    // Session SC2C: the newest groupable quotes from the server (the status filter runs there), less the ones already in this portfolio.
    void api.quotes
      .listPage({ filter: "groupable", limit: GROUPABLE_PAGE, offset: 0 })
      .then((r) => setQuotes(r.items.filter((q) => !existing.has(q.id))))
      .catch(() => setQuotes([]));
  }, [existing]);
  const available = quotes ?? [];
  if (available.length === 0) return null;
  return (
    <div className={CARD + " mt-6"}>
      <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-blue">Add a site quote</p>
      <div className="flex flex-wrap gap-2">
        {available.map((q) => (
          <button key={q.id} type="button" className={BTN} onClick={async () => { await api.quotePortfolios.attachQuote(portfolioId, q.id); await onChanged(); }}>
            {q.quoteRef} · {q.site?.name ?? q.customerName ?? "No site"}
          </button>
        ))}
      </div>
    </div>
  );
}

function DeletePortfolioModal({ id, name, onCancel, onDeleted }: { id: string; name: string; onCancel: () => void; onDeleted: () => void }) {
  const [mode, setMode] = useState<"detach" | "delete">("detach");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await api.quotePortfolios.delete(id, mode === "delete");
      onDeleted();
    } catch {
      setError("Could not delete the portfolio.");
      setBusy(false);
    }
  };
  return (
    <ModalShell ariaLabel={`Delete ${name}`} onClose={() => !busy && onCancel()} closeOnBackdrop width="max-w-md" border="border-ele-danger" panelClassName="p-6">
        <h2 className="mb-2 font-heading text-xl text-ele-strong">Delete {name}</h2>
        <p className="mb-4 text-sm text-ele-body">Choose what happens to the grouped site quotes.</p>
        {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger p-3 text-sm text-ele-strong">{error}</p>}
        <label className="mb-2 flex cursor-pointer items-start gap-3 rounded-ele border border-ele-purple-accent p-3">
          <input type="radio" name="delmode" checked={mode === "detach"} onChange={() => setMode("detach")} className="mt-1 accent-ele-green" />
          <span className="text-sm text-ele-strong">Keep the quotes<span className="block text-ele-body">Detach every quote and delete only the portfolio grouping.</span></span>
        </label>
        <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-ele border border-ele-purple-accent p-3">
          <input type="radio" name="delmode" checked={mode === "delete"} onChange={() => setMode("delete")} className="mt-1 accent-ele-danger" />
          <span className="text-sm text-ele-strong">Delete the quotes too<span className="block text-ele-body">Delete draft and unsigned quotes as well. Signed quotes are kept and detached.</span></span>
        </label>
        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" className={`${BTN} ${MODAL_ACTION_BTN}`} onClick={onCancel} disabled={busy}>Cancel</button>
          {mode === "delete" ? (
            // The destructive multi-quote hard delete arms on the first click and fires on the
            // second (Session AEH A2, the ACH two-step platform standard), so a single click can
            // never delete quotes. The reversible "keep the quotes" mode below stays single-click.
            <ConfirmButton
              label="Delete portfolio and quotes"
              confirmLabel="Confirm permanent delete?"
              busy={busy}
              busyLabel="Deleting..."
              onConfirm={() => void confirm()}
              className={buttonClasses("destructive", "md", MODAL_ACTION_BTN)}
            />
          ) : (
            <Button variant="destructive" className={MODAL_ACTION_BTN} onClick={confirm} disabled={busy}>{busy ? "Deleting..." : "Delete portfolio"}</Button>
          )}
        </div>
    </ModalShell>
  );
}
